import { useState, useEffect } from 'react';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { Select } from '@@/form-components/ReactSelect';

interface ThresholdInputProps {
  name: string;
  value: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  error?: string;
  metricType: 'percentage' | 'bytes' | 'raw';
  'data-cy'?: string;
}

type ByteUnit = 'B' | 'KB' | 'MB' | 'GB' | 'TB';

const byteUnitOptions = [
  { value: 'B', label: 'Bytes', multiplier: 1 },
  { value: 'KB', label: 'KB', multiplier: 1024 },
  { value: 'MB', label: 'MB', multiplier: 1024 * 1024 },
  { value: 'GB', label: 'GB', multiplier: 1024 * 1024 * 1024 },
  { value: 'TB', label: 'TB', multiplier: 1024 * 1024 * 1024 * 1024 },
];

export function convertBytesToHumanReadable(bytes: number): {
  value: number;
  unit: ByteUnit;
} {
  if (bytes === 0) return { value: 0, unit: 'B' };
  const unitsDesc = [...byteUnitOptions].reverse();
  const exactMatch = unitsDesc.find((u) => {
    const converted = bytes / u.multiplier;
    return converted >= 1 && Number.isInteger(converted);
  });
  if (exactMatch) {
    return {
      value: bytes / exactMatch.multiplier,
      unit: exactMatch.value as ByteUnit,
    };
  }
  const firstAboveOne = unitsDesc.find((u) => bytes / u.multiplier >= 1);
  if (firstAboveOne) {
    const converted = bytes / firstAboveOne.multiplier;
    return {
      value: Math.round(converted * 100) / 100,
      unit: firstAboveOne.value as ByteUnit,
    };
  }
  return { value: bytes, unit: 'B' };
}

export function ThresholdInput({
  name,
  value,
  onChange,
  onBlur,
  error,
  metricType,
  'data-cy': dataCy,
}: ThresholdInputProps) {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [selectedUnit, setSelectedUnit] = useState<ByteUnit>('MB');

  // Initialize display values based on the raw threshold value
  useEffect(() => {
    if (metricType === 'bytes') {
      if (value === 0) {
        // Keep the default unit (MB) so user input is treated as MB by default.
        setDisplayValue('0');
        return;
      }
      const { value: convertedValue, unit } =
        convertBytesToHumanReadable(value);
      setDisplayValue(convertedValue.toString());
      setSelectedUnit(unit);
      return;
    }
    setDisplayValue(value.toString());
  }, [value, metricType]);

  function handleDisplayValueChange(newDisplayValue: string) {
    setDisplayValue(newDisplayValue);

    const numericValue = parseFloat(newDisplayValue);
    if (Number.isNaN(numericValue)) {
      onChange(0);
      return;
    }

    if (metricType === 'bytes') {
      const unitConfig = byteUnitOptions.find((u) => u.value === selectedUnit);
      const bytesValue = Math.round(
        numericValue * (unitConfig?.multiplier || 1)
      );
      onChange(bytesValue);
    } else {
      onChange(numericValue);
    }
  }

  function handleUnitChange(unit: ByteUnit) {
    setSelectedUnit(unit);

    const numericValue = parseFloat(displayValue);
    if (Number.isNaN(numericValue)) return;

    const unitConfig = byteUnitOptions.find((u) => u.value === unit);
    const bytesValue = Math.round(numericValue * (unitConfig?.multiplier || 1));
    onChange(bytesValue);
  }

  function getLabel() {
    switch (metricType) {
      case 'percentage':
        return 'Threshold (%)';
      case 'bytes':
        return 'Threshold';
      default:
        return 'Threshold';
    }
  }

  function getTooltip() {
    switch (metricType) {
      case 'percentage':
        return 'The percentage threshold for the condition (0-100)';
      case 'bytes':
        return `The threshold value for the condition. Raw value: ${value.toLocaleString()} bytes`;
      default:
        return 'The threshold value for the condition';
    }
  }

  function getPlaceholder() {
    switch (metricType) {
      case 'percentage':
        return 'Enter percentage (e.g., 80)';
      case 'bytes':
        return 'Enter value (e.g., 500)';
      default:
        return 'Enter threshold';
    }
  }

  return (
    <FormControl
      label={getLabel()}
      inputId="threshold-input"
      errors={error}
      required
      tooltip={getTooltip()}
    >
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            id="threshold-input"
            name={name}
            type="number"
            value={displayValue}
            onChange={(e) => handleDisplayValueChange(e.target.value)}
            onBlur={onBlur}
            placeholder={getPlaceholder()}
            data-cy={dataCy || 'threshold-input'}
            min={metricType === 'percentage' ? 0 : undefined}
            max={metricType === 'percentage' ? 100 : undefined}
            step={metricType === 'bytes' ? 'any' : 1}
          />
        </div>

        {metricType === 'bytes' && (
          <div className="w-20">
            <Select
              id="threshold-unit-select"
              options={byteUnitOptions}
              value={byteUnitOptions.find(
                (unit) => unit.value === selectedUnit
              )}
              onChange={(option) =>
                option && handleUnitChange(option.value as ByteUnit)
              }
              isSearchable={false}
              data-cy="threshold-unit-select"
            />
          </div>
        )}

        {metricType === 'percentage' && (
          <div className="flex items-center px-3 text-gray-600">%</div>
        )}
      </div>

      {metricType === 'bytes' && (
        <div className="text-xs text-gray-500 mt-1">
          Raw value: {value.toLocaleString()} bytes
        </div>
      )}
    </FormControl>
  );
}
